// Продукт на НЯКОЛКО станции (напр. „Пилешко филе" на Скара + Студена кухня):
// BE праща реда в канала на всяка негова станция и всяка станция има СВОЙ
// статус (`status` в канала ѝ), а общият е `rowStatus`. Тук са чистите
// правила, по които кухненският екран го показва — изнесени от
// MonitorsContent, за да се тестват и да не растe конвейерът.
//
//  * ОБИКНОВЕН монитор: редът е ОТДЕЛНА ЧАСТ за всеки канал, от който идва —
//    монитор с двете станции го показва два пъти (всяка със своя статус), а
//    „Изпълни"/„Върни"/„В процес" пращат `stationId` на частта.
//  * ШПИКЕР: редът е ЕДИН (издава се ястието, не частта), статусът му е
//    общият `rowStatus`, а действията са без `stationId` (важат за всички части).
//
// Ред с една станция минава през всичко това НЕПРОМЕНЕН — ключовете и
// заявките му са байт за байт досегашните.
import type { OrderItem, StationPart } from "./types";
import { getRowId, isMultiStation } from "./types";

// Елементите от канала на една станция, с помнене откъде са дошли. На
// шпикера статусът на ред с няколко станции става общият — оттук нататък
// целият конвейер (табове, иконки, селекция) вижда само него.
export const tagFeedStation = (
  elements: OrderItem[],
  feedStationId: number,
  isShpicker: boolean,
): OrderItem[] =>
  elements.map((element) =>
    isShpicker && isMultiStation(element) && element.rowStatus
      ? { ...element, feedStationId, status: element.rowStatus }
      : { ...element, feedStationId },
  );

// Шпикерът вижда ред на няколко станции ВЕДНЪЖ на целия екран — и при
// разделен екран, където станциите му са в различни панели (иначе всеки панел
// би показал ястието, и двата биха го „издали"). Редът остава само в канала
// на „собственика": първата от станциите му (основната е първа), която
// мониторът слуша. Ред с една станция и обикновен монитор — без промяна.
export const keepOnShpicker = (
  item: OrderItem,
  feedStationId: number,
  listenedStationIds: number[],
): boolean => {
  if (!isMultiStation(item)) return true;
  const owner = item.stationIds?.find((stationId) =>
    listenedStationIds.includes(stationId),
  );
  return owner == null || owner === feedStationId;
};

// Станцията на ЧАСТТА, която представя редът на този екран; null за ред с
// една станция и за шпикера (там редът е цял).
export const partStationIdOf = (
  item: OrderItem,
  isShpicker: boolean,
): number | null =>
  !isShpicker && isMultiStation(item) && item.feedStationId != null
    ? item.feedStationId
    : null;

// Ключът, по който се дедупликира един физически ред в поръчката. За част от
// ред на няколко станции се добавя станцията — двете части са различни редове
// на екрана, не повторна доставка.
export const productKeyOf = (item: OrderItem, isShpicker: boolean): string => {
  const modifiersKey =
    item.modifiers
      ?.map((modifier) => modifier.id)
      .sort()
      .join(",") || "";
  const rowId = getRowId(item);
  const baseKey = `${item.itemId}-${item.isAddition}-${modifiersKey}-${item.hasAdditions}-${item.isCancelled}-${rowId ?? "legacy"}`;
  const partStationId = partStationIdOf(item, isShpicker);
  return partStationId == null ? baseKey : `${baseKey}@${partStationId}`;
};

// Ключът на локалния прогрес по бройки (`completedQuantities`). Всяка част
// брои своите бройки отделно.
export const completedQuantityKeyOf = (product: {
  itemId: number;
  billId: number;
  billElementId: number;
  billElementItemId: number | null;
  stationId: number | null;
}): string => {
  const baseKey = `${product.itemId}-${product.billId}-${product.billElementId}-${product.billElementItemId ?? "legacy"}`;
  return product.stationId == null
    ? baseKey
    : `${baseKey}@${product.stationId}`;
};

// Основните продукти в реда на маркиране, всяка добавка — непосредствено
// под своя родител (с изчистен подтекст „към …", защото родителят се вижда),
// а добавките без видим родител — накрая, в оригиналния си ред.
//
// Когато един основен ред е на екрана в няколко части (обикновен монитор с
// няколко от станциите му), добавката отива под частта от СВОЯ канал, иначе
// под първата — никога под всяка част (иначе би се показала два пъти).
// Стари payload-и без ид на реда пазят досегашното поведение.
export const attachAdditions = (
  mainProducts: OrderItem[],
  additionProducts: OrderItem[],
): OrderItem[] => {
  const preferredMainOf = (addition: OrderItem): OrderItem | undefined => {
    const candidates = mainProducts.filter(
      (main) => main.mainBillElementItemId === addition.mainBillElementItemId,
    );
    return (
      candidates.find((main) => main.feedStationId === addition.feedStationId) ??
      candidates[0]
    );
  };

  const reordered: OrderItem[] = [];
  // Matched се следи по САМИЯ ред, не по itemId — същата добавка от менюто
  // може да стои и под продукт от тази станция, и към cross-station
  // продукт; по itemId втората се броеше за показана и изчезваше.
  const matchedAdditions = new Set<OrderItem>();

  mainProducts.forEach((mainItem) => {
    reordered.push(mainItem);
    additionProducts.forEach((addition) => {
      if (addition.mainBillElementItemId !== mainItem.mainBillElementItemId) {
        return;
      }
      if (
        addition.mainBillElementItemId != null &&
        preferredMainOf(addition) !== mainItem
      ) {
        return;
      }
      reordered.push({
        ...addition,
        mainProductName: null,
        mainStationName: null,
      });
      matchedAdditions.add(addition);
    });
  });

  reordered.push(
    ...additionProducts.filter((addition) => !matchedAdditions.has(addition)),
  );
  return reordered;
};

// Докъде е една част — за подтекста под името.
export type StationPartState = "done" | "inProgress" | "waiting" | "untracked";

export const stationPartStateOf = (part: StationPart): StationPartState => {
  if (part.tracked === false) return "untracked";
  if (part.status === "2" || part.status === "3") return "done";
  if (part.status === "1") return "inProgress";
  return "waiting";
};

export interface StationHintPart {
  stationId: number;
  state: StationPartState;
}

// ОБИКНОВЕН монитор: частите на ДРУГИТЕ станции на реда — тези, които този
// панел не гледа (гледа ли ги, те са отделни редове с етикет). Скарата вижда
// „+ Студена кухня · готово ✓" и знае кога да сервира.
export const otherStationParts = (
  item: OrderItem,
  paneStationIds: number[],
): StationHintPart[] =>
  isMultiStation(item)
    ? (item.stationStatuses ?? [])
        .filter((part) => !paneStationIds.includes(part.stationId))
        .map((part) => ({
          stationId: part.stationId,
          state: stationPartStateOf(part),
        }))
    : [];

// ШПИКЕР при „всички статуси": кои станции са готови с ястието, докато то
// самото още не е готово (готовото е цяло — подтекст там е излишен). Станция
// без кухненски монитор не държи ястието и не се показва.
export const shpickerStationParts = (item: OrderItem): StationHintPart[] =>
  isMultiStation(item) && item.rowStatus !== "2" && item.rowStatus !== "3"
    ? (item.stationStatuses ?? [])
        .filter((part) => part.tracked !== false)
        .map((part) => ({
          stationId: part.stationId,
          state: stationPartStateOf(part),
        }))
    : [];
